/**
 * Authored-identity projection + field helpers for workflow nodes — the
 * crypto-free core shared by {@link module:spec/node-hash} (which fingerprints
 * the projection) and the `pull` serializer (which renders the fields).
 *
 * This module deliberately imports **no Node built-ins** so it is safe to load
 * in a browser bundle (e.g. the flow designer's cofactor.md view, which renders
 * specs client-side). The actual hashing — the only part that needs
 * `node:crypto` — lives in `node-hash.ts`, which imports from here.
 *
 * @module spec/authored-fields
 */

/** Minimal node shape both `EmittedWorkflow.nodes` and `LiveNode` satisfy. */
export interface HashableNode {
  nodeType: string;
  configJson: Record<string, unknown> | null;
}

/**
 * Extract the authorable slice of a node's config, normalized so that a live
 * node and its round-tripped (`pull` → parse → `emit`) form project to the
 * same object. Per-type, mirroring what `emit.ts` writes and what `parse.ts`
 * can read back — anything the grammar can't express is dropped.
 */
export function authoredProjection(
  nodeType: string,
  raw: Record<string, unknown> | null
): Record<string, unknown> {
  const c = raw ?? {};
  switch (nodeType) {
    case 'login':
      // Identity is the credential being assumed, across all binding modes:
      // static (credentialName/Id), from_template (credentialTemplateName/Id),
      // and from_seed (seedCredentialRef name/dataKey). `performLogin` is
      // excluded — the injected leading login omits it while a live login may
      // set it, and login nodes are never cached.
      return { credential: canon(loginCredentialRef(c)) };
    case 'navigate':
      return {
        pageUrl: canon(str(c.pageUrl)),
        openInNewTab: c.openInNewTab === true,
      };
    case 'action':
      return {
        title: authoredActionTitle(c),
        // emit falls back actionDescription → title; trim because the blockquote
        // round-trip can't preserve leading/trailing whitespace.
        description:
          trimText(str(c.actionDescription) ?? str(c.description)) ?? authoredActionTitle(c),
        // Authorable name/type/value triples — server-assigned labels are
        // dropped, and value is sourced from inputValues/formFields alike.
        fields: authoredActionFields(c),
        captures: arr(c.declaredOutputs).filter((x): x is string => typeof x === 'string'),
        // Drop assertion `id` (random UUID) — only the semantic content matters.
        assertions: arr(c.inlineAssertions).map((a) => ({
          condition: str(prop(a, 'condition')) ?? '',
          severity: str(prop(a, 'severity')) ?? 'fail',
          agentic: prop(a, 'agentic') === true,
        })),
        captureAudio: c.captureAudio === true,
        captureLabel: str(c.captureLabel) ?? null,
        captureExtraMs: num(c.captureExtraMs) ?? null,
        alwaysUseAgent: c.alwaysUseAgent === true,
      };
    case 'assertion':
      return {
        condition: canon(str(c.condition)),
        description: canon(str(c.description) ?? str(c.condition)),
        // Legacy `strict:true` is equivalent to severity 'fail' at runtime; map
        // it so a strict assertion doesn't silently weaken to 'warn'.
        severity: str(c.severity) ?? (c.strict === true ? 'fail' : 'warn'),
        expectedOutcome: str(c.expectedOutcome) ?? null,
        alwaysUseAgent: c.alwaysUseAgent === true,
      };
    case 'seed':
      // Canonical keys are snapshotVersion/onFailure/executionMode; the seed*-
      // prefixed names are deprecated tool-input aliases. Read canonical first,
      // fall back to alias so emitted (alias) and live (canonical) both resolve.
      return {
        specName: canon(str(c.seedWorkflowName)),
        snapshotVersion: c.snapshotVersion ?? c.seedSnapshotVersion ?? null,
        onFailure: str(c.onFailure) ?? str(c.seedOnFailure) ?? null,
        executionMode: str(c.executionMode) ?? str(c.seedExecutionMode) ?? null,
        scope: str(c.scope) ?? null,
        runTeardown: typeof c.runTeardown === 'boolean' ? c.runTeardown : null,
        // Input overrides change what the seed provisions — authored identity.
        inputValues: seedInputValues(c),
      };
    case 'setup':
      return {
        specName: canon(str(c.setupFlowName)),
        snapshotVersion: c.snapshotVersion ?? null,
        mode: str(c.executionMode) ?? null,
      };
    case 'wait':
      return { delaySeconds: num(c.delaySeconds) ?? num(c.waitDuration) ?? null };
    case 'condition':
      // `__specBranches` is a documentation marker (runtime ignores it); the
      // real branch nodes are flat siblings keyed by their own @id.
      // Runtime + UI store the expression in `conditionDescription`;
      // `conditionExpression` is a deprecated tool-input alias.
      return {
        expression: canon(
          str(c.conditionDescription) ?? str(c.conditionExpression) ?? str(c.description)
        ),
        hints: str(c.evaluationHints) ?? null,
      };
    case 'notification':
      return {
        channel: str(c.channel) ?? null,
        to: str(c.to) ?? null,
        subject: str(c.subject) ?? null,
        body: str(c.body) ?? null,
        attach: str(c.attachmentRef) ?? null,
      };
    case 'audio':
      // parse only recognizes `loop: true` and `waitForCompletion: false`, so
      // normalize to those defaults (absent loop ≡ false, absent wait ≡ true).
      return {
        attachmentRef: str(c.attachmentRef) ?? null,
        loop: c.loop === true,
        waitForCompletion: c.waitForCompletion !== false,
        label: str(c.label) ?? null,
      };
    case 'audio-assertion': {
      const det = obj(c.deterministic);
      const nl = obj(c.naturalLanguage);
      return {
        label: str(c.label) ?? null,
        severity: str(c.severity) ?? 'fail',
        prompt: str(nl?.prompt) ?? str(c.prompt) ?? null,
        model: str(nl?.model) ?? str(c.model) ?? null,
        minDurationMs: num(det?.minDurationMs) ?? num(c.minDurationMs) ?? null,
        maxDurationMs: num(det?.maxDurationMs) ?? num(c.maxDurationMs) ?? null,
        minRmsDb: num(det?.minRmsDb) ?? num(c.minRmsDb) ?? null,
        silenceFractionMax: num(det?.silenceFractionMax) ?? num(c.silenceFractionMax) ?? null,
        reference: str(c.referenceAttachmentRef) ?? null,
        envelopeCorrelationMin:
          num(det?.envelopeCorrelationMin) ?? num(c.envelopeCorrelationMin) ?? null,
      };
    }
    case 'api_sequence':
      // Opaque: the real config is an API-request-sequence DSL (steps, exports,
      // stopOnFailure) that the `.canary.md` grammar can't yet express. Hash to
      // a constant so the node is never reported changed — the round-trip
      // leaves the server config untouched rather than overwriting it with a
      // lossy spec form. Full DSL round-trip is a follow-up.
      return {};
    case 'sandbox_action': {
      const params = obj(c.params) ?? {};
      const paramEntries: Record<string, string | null> = {};
      for (const key of Object.keys(params).sort()) {
        paramEntries[key] = str(params[key]) ?? null;
      }
      const outputKeys = Array.isArray(c.outputKeys)
        ? c.outputKeys.filter((k): k is string => typeof k === 'string').sort()
        : [];
      return {
        actionSlug: str(c.actionSlug) ?? null,
        label: str(c.actionLabel) ?? null,
        params: paramEntries,
        outputs: outputKeys,
      };
    }
    case 'mcp': {
      // Authorable slice of an mcp node, mirroring the `**MCP — …**` grammar.
      // Identity fields are compared name-first (applicationName /
      // credentialTemplateName) with id fallback — like `loginCredentialRef` —
      // so a live node (name + resolved id persisted by the server's write
      // chokepoint) hash-matches its round-tripped, name-only spec form.
      // Runtime artifacts (playbookSteps, alwaysUseAgent — no spec syntax) are
      // deliberately excluded so they never flag a node as changed.
      const declaredOutputs = Array.isArray(c.declaredOutputs)
        ? c.declaredOutputs.filter((k): k is string => typeof k === 'string').sort()
        : [];
      const toolAllowlist = Array.isArray(c.toolAllowlist)
        ? c.toolAllowlist.filter((t): t is string => typeof t === 'string').sort()
        : [];
      return {
        title: trimText(str(c.title)) ?? 'MCP',
        application: canon(mcpApplicationRef(c)),
        persona: canon(mcpPersonaRef(c)),
        // Instructions round-trip through a blockquote (which trims), not
        // stripPunctuation — so trim, don't canon.
        instructions: trimText(str(c.instructions)),
        outputs: declaredOutputs,
        // Normalize empty → same as absent (pull omits the bullet either way).
        toolAllowlist: toolAllowlist.length > 0 ? toolAllowlist : null,
      };
    }
    case 'end':
      return { outcome: str(c.outcome) ?? 'pass' };
    default:
      // Unknown node type — hash on type alone so a config change can't
      // silently masquerade as unchanged.
      return {};
  }
}

/**
 * The lead-in title for an action. Prefers the authored `customActionTitle`;
 * for agent-authored actions that carry only a description, derives a stable,
 * single-line title from the first sentence (the `.canary.md` grammar requires
 * a title in `**Action — …**`). Shared by the hash and the `pull` serializer
 * so a title-less action round-trips without inventing a new title each time.
 */
export function authoredActionTitle(raw: Record<string, unknown> | null): string {
  const c = raw ?? {};
  const explicit = str(c.customActionTitle) ?? str(c.title);
  if (explicit && explicit.trim()) return explicit.trim();
  const desc = (str(c.actionDescription) ?? '').replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  if (!desc) return 'Action';
  const sentence = desc.split(/(?<=[.!?])\s/)[0] ?? desc;
  return sentence.slice(0, 80).trim() || 'Action';
}

/**
 * The authorable form fields of an action, as `.canary.md` `- field` lines
 * express them. An action carries its fields across up to three keys:
 *   - `dataRequirements` — the field schema (name, type, label, required);
 *   - `inputValues` — pre-filled values, keyed by field name;
 *   - `formFields` — the legacy combined shape (name, label, type, value).
 * `emit` writes all three; a UI/agent-built node may carry only some. We key
 * off dataRequirements when present (else formFields) for name/type, and read
 * the value from inputValues first, falling back to formFields. Shared by the
 * hash and the `pull` serializer so both agree on the round-trip.
 */
export function authoredActionFields(
  raw: Record<string, unknown> | null
): Array<{ name: string; type: string; value: string }> {
  const c = raw ?? {};
  const inputValues =
    c.inputValues && typeof c.inputValues === 'object'
      ? (c.inputValues as Record<string, unknown>)
      : {};
  const formFields = arr(c.formFields);
  const formByName = new Map<string, unknown>();
  for (const f of formFields) {
    const n = str(prop(f, 'name'));
    if (n) formByName.set(n, f);
  }
  const reqs = arr(c.dataRequirements);
  const source = reqs.length > 0 ? reqs : formFields;
  const out: Array<{ name: string; type: string; value: string }> = [];
  for (const s of source) {
    const name = str(prop(s, 'name'));
    if (!name) continue;
    const value = str(inputValues[name]) ?? str(prop(formByName.get(name), 'value')) ?? '';
    out.push({ name, type: str(prop(s, 'type')) ?? 'text', value });
  }
  return out;
}

/**
 * The credential a login assumes, as a single display string, across every
 * binding mode. Shared by the hash and the `pull` serializer so a login
 * round-trips without churn regardless of how its credential is bound.
 */
export function loginCredentialRef(raw: Record<string, unknown> | null): string | undefined {
  const c = raw ?? {};
  const seedRef = c.seedCredentialRef;
  return (
    str(c.credentialTemplateName) ??
    str(c.credentialName) ??
    str(c.credentialId) ??
    str(c.credentialTemplateId) ??
    // For from_seed bindings, `dataKey` is the stable identifier (matches the
    // seed's declared-output key); `name` is a display label that can drift.
    // Prefer dataKey so spec round-trip (which uses `seed-output <dataKey>`)
    // hash-matches the live row regardless of label.
    str(prop(seedRef, 'dataKey')) ??
    str(prop(seedRef, 'name'))
  );
}

/**
 * The MCP application an `mcp` node targets, name-first with propertyId
 * fallback. Shared by the hash and the `pull` serializer so both agree on the
 * round-trip regardless of whether the live config carries the resolved id,
 * the name, or both.
 */
export function mcpApplicationRef(raw: Record<string, unknown> | null): string | undefined {
  const c = raw ?? {};
  return str(c.applicationName) ?? str(c.propertyId);
}

/**
 * The persona (credential template) an `mcp` node authenticates with,
 * name-first with id fallback (template preferred over the direct-credential
 * escape hatch). Shared by the hash and the `pull` serializer.
 */
export function mcpPersonaRef(raw: Record<string, unknown> | null): string | undefined {
  const c = raw ?? {};
  return str(c.credentialTemplateName) ?? str(c.credentialTemplateId) ?? str(c.credentialId);
}

/** Recursively sort object keys for order-independent serialization. */
export function sortObjectKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortObjectKeys);
  if (value !== null && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return Object.keys(obj)
      .sort()
      .reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = sortObjectKeys(obj[k]);
        return acc;
      }, {});
  }
  return value;
}

/**
 * Canonicalize a value the way the parser's `stripPunctuation` does (drops
 * surrounding backticks/quotes and a single trailing period). Fields read
 * back through that path — assertion/condition text, navigate URLs, seed/setup
 * names — must hash post-strip so a live value ending in '.' still matches its
 * round-tripped form. Returns null for absent input.
 */
function canon(v: string | undefined): string | null {
  if (v === undefined) return null;
  return v.replace(/[`"]/g, '').replace(/\.$/, '').trim();
}

/** Trim surrounding whitespace; null for absent input. */
function trimText(v: string | undefined): string | null {
  return v === undefined ? null : v.trim();
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

/**
 * Seed node input overrides as a string-valued record; null when absent or
 * empty so a missing map and `{}` hash identically.
 */
function seedInputValues(c: Record<string, unknown>): Record<string, string> | null {
  if (!c.inputValues || typeof c.inputValues !== 'object') return null;
  const entries = Object.entries(c.inputValues as Record<string, unknown>).filter(
    (e): e is [string, string] => typeof e[1] === 'string'
  );
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function prop(v: unknown, key: string): unknown {
  return v && typeof v === 'object' ? (v as Record<string, unknown>)[key] : undefined;
}

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}
